import { SoundButton } from "../../../common/SoundButton/SoundButton";
import toast from "react-hot-toast";
import { autospinDelay } from "../../../../logic/helpers/spinBet";
// src/components/pages/MainPage/SlotMachine/SlotMachine.tsx
//
// Beach slot machine. The reel DOM structure, CSS animation classes and the
// whole stop-timing state machine are a 1:1 port of the alpha-mini-app client
// (see SlotMachine.scss for the matching keyframes):
//
//   1. press SPIN     -> every reel gets the `spin` class: a 0.1s linear
//                        infinite translateY loop under blur(10px)
//   2. outcome known  -> reel symbols swap mid-blur (invisible), phase
//                        staggered 0/50/100ms
//   3. per reel i     -> after REEL_STOP_DELAYS_MS[i], class becomes
//                        `finish-spin` with an inline duration of
//                        REEL_STOP_DURATIONS_MS[i]: the reel drops in from
//                        -1.5 window heights and overshoot-bounces to rest
//                        (cubic-bezier(.35,1.75,.75,1))
//   4. jackpot streak -> while reels so far all show the golden shell, the
//                        next reel's delay is multiplied by rand(reelNo,10)
//                        and its duration by rand(1,1.1*reelNo); a full
//                        jackpot holds 1s and scales the shells up 1.5x
//   5. settle         -> 10ms after the last reel rests the classes reset,
//                        then the win banner appears

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useSlice } from "@bridge-applications/provider-kit";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faGear,
    faMoneySimpleFromBracket,
} from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import type {
    ApiSmSpinChaseResult,
    ApiSmSpinV2Res as ApiSmSpinRes,
    ApiSmSpinTogetherBoost,
    ApiSmStateSnapshot,
    ApiWithdrawalCurrencyCode,
} from "@bridge-applications/pb-types";
import { haptics } from "../../../../logic/helpers/haptics";
import {
    JACKPOT_HOLD_MS,
    JACKPOT_SYMBOL,
    REEL_STOP_DELAYS_MS,
    REEL_STOP_DURATIONS_MS,
    SPIN_RESOLVE_DELAY_MS,
    buildReelDisplays,
    formatCompact,
    getRandom,
} from "../../../../logic/helpers/slotMachine";
import { hasSpinEnergy } from "../../../../logic/helpers/slotMachineState";
import type {
    SlotSymbol,
    SpinPrize,
    SpinResult,
} from "../../../../logic/helpers/slotMachine";
import {
    SlotMachineApiError,
    useSlotMachine,
} from "../../../../logic/hooks/useSlotMachine";
import {
    mergeSpinResponseSnapshot,
    type ClientSpinResponse,
} from "../../../../logic/helpers/slotMachineSnapshot";
import woodenBoard from "../../../../assets/static/images/wooden-board.webp";
// import slotMachineTitle from "../../../../assets/static/images/decos/deco-slotmachine-title.webp";
import slotMachineTitle from "../../../../assets/static/images/decos/deco-pebbler-palm-logomark.webp";

import slotMachineFrame from "../../../../assets/static/images/slot-machine-frame.webp";
import beachCoinsIcon from "../../../../assets/static/images/symbol-beach-coins.webp";
import symbolSwimmingTrunks from "../../../../assets/static/images/symbols/v1/symbol-swimming-trunks.webp";
import symbolCoconutDrink from "../../../../assets/static/images/symbols/v1/symbol-coconut-drink.webp";
import symbolSunglasses from "../../../../assets/static/images/symbols/v1/symbol-sunglasses.webp";
import symbolBeachBall from "../../../../assets/static/images/symbols/v1/symbol-beach-ball.webp";
import symbolStarfish from "../../../../assets/static/images/symbols/v1/symbol-starfish.webp";
import symbolShell from "../../../../assets/static/images/symbols/v1/symbol-shell.webp";
import symbolGoldenBeachCoins from "../../../../assets/static/images/symbols/v1/symbol-golden-beach-coins.webp";
import symbolSingleBeachCoin from "../../../../assets/static/images/symbols/v1/symbol-single-beach-coin.webp";
import symbolEnergyDrink from "../../../../assets/static/images/symbols/v1/symbol-energy-drink.webp";
import symbolGoldenShell from "../../../../assets/static/images/symbols/v1/symbol-golden-shell.webp";
import symbolThief from "../../../../assets/static/images/symbols/v1/symbol-thief.webp";
import symbolFriendSteal from "../../../../assets/static/images/symbols/v1/symbol-friend-steal.webp";
import tickerLeft from "../../../../assets/static/images/slot/ticker-left.webp";
import tickerRight from "../../../../assets/static/images/slot/ticker-right.webp";
import gramRoundLogo from "../../../../assets/static/images/onchain/coins/round/gram-toncoin-round-logo.webp";
import notcoinRoundLogo from "../../../../assets/static/images/onchain/coins/round/notcoin-round-logo.webp";
import usdtRoundLogo from "../../../../assets/static/images/onchain/coins/round/usdt-round-logo.webp";
import {
    playSound,
    playTimedFadeSound,
    preloadSounds,
} from "../../../../logic/helpers/audio";
import { formatHudMinorUnits } from "../../../../logic/helpers/currencyAmount";
import {
    clearPrizeParticleCluster,
    generatePrizeParticles,
    preloadPrizeParticleImages,
} from "../../../../logic/helpers/prizeParticles";
import sfxReelsWhirring from "../../../../assets/static/audio/slot-machine-reels-whirring.mp3";
import sfxReelStop from "../../../../assets/static/audio/slot-machine-reel-stop.mp3";
import sfxCoinsFalling from "../../../../assets/static/audio/slot-machine-coins-falling.mp3";
import sfxEscalation1 from "../../../../assets/static/audio/slot-machine-escalation-1.mp3";
import sfxEscalation2 from "../../../../assets/static/audio/slot-machine-escalation-2.mp3";
import sfxEscalation3 from "../../../../assets/static/audio/slot-machine-escalation-3.mp3";
import sfxPostEscalation from "../../../../assets/static/audio/slot-machine-post-escalation-music.mp3";
import sfxEnergyWin from "../../../../assets/static/audio/slot-machine-splash-sound-2.mp3";
import sfxEvilLaugh from "../../../../assets/static/audio/slot-machine-evil-laugh.mp3";
import friendStealMusic from "../../../../assets/static/audio/slot9-main-bgm-2.mp3";
import betSizeChangeSound from "../../../../assets/static/audio/slot/bet-size-change.mp3";
import { useAutospin } from "../../../../logic/hooks/useAutospin";
import { useTelegramWriteAccess } from "../../../../logic/hooks/useTelegramWriteAccess";
import { usePlayerFeedback } from "../../../../logic/hooks/usePlayerFeedback";
import { useBetMultiplier } from "../../../../logic/hooks/useBetMultiplier";
import { SpinButton } from "./components/SpinButton/SpinButton";
import { BetMultiplierBanner } from "./components/BetMultiplierBanner/BetMultiplierBanner";
import { SymbolChase } from "./SymbolChase";
import { SpinTogetherBoostIndicator } from "./FriendsRail/components/SpinTogetherBoostIndicator/SpinTogetherBoostIndicator";
import { SettingsDialog } from "../SettingsDialog";
import { WithdrawalDialog } from "../WithdrawalDialog";
import FriendsRail from "./FriendsRail/FriendsRail";
import type { SpinTogetherController } from "../../../../logic/hooks/useSpinTogether";
import "./SlotMachine.scss";

// ___per-reel escalation stingers for a high-value near-miss streak
// (alpha-mini-app: ass-1/2/3.mp3)
const SFX_ESCALATIONS = [sfxEscalation1, sfxEscalation2, sfxEscalation3];

// ___the reels-whirring bed sits under the stingers, so keep it quieter
const WHIRRING_VOLUME = 0.55;

const FRIEND_STEAL_PARTICLE_SCALE = 0.65;
const FRIEND_STEAL_MUSIC_DURATION_MS = 7_000;
const FRIEND_STEAL_MUSIC_FADE_OUT_MS = 4_000;

// Temporarily hide crypto balance chips in the slot-machine HUD.
const SHOW_ONCHAIN_BALANCES = false;

// ___high-value symbols that get the escalation-sound + stretched-suspense
// treatment on a streak/near-miss. alpha-mini-app only did this for its single
// jackpot symbol; here the top three tiers all build tension. The full
// jackpot celebration (music + scale-up) stays exclusive to the golden shell.
const ESCALATION_SYMBOLS: SlotSymbol[] = [
    "golden-shell", // $10k USDt jackpot
    "shell", // 10M beach-coins
    "golden-beach-coins", // 50-150M beach-coins
];

const ALL_SFX = [
    betSizeChangeSound,
    sfxReelsWhirring,
    sfxReelStop,
    sfxCoinsFalling,
    ...SFX_ESCALATIONS,
    sfxPostEscalation,
    sfxEnergyWin,
    sfxEvilLaugh,
    friendStealMusic,
];

const SLOT_PRIZE_PARTICLE_CLUSTER = "slots";

const ONCHAIN_PRIZE_PARTICLE_IMAGES = [
    gramRoundLogo,
    notcoinRoundLogo,
    usdtRoundLogo,
] as const;

const ONCHAIN_BALANCE_ASSETS = [
    {
        currencyCode: "GRAM",
        decimalScale: 9,
        sortScale: 1n,
        label: "GRAM",
        icon: gramRoundLogo,
    },
    {
        currencyCode: "NOT",
        decimalScale: 9,
        sortScale: 1n,
        label: "Notcoin",
        icon: notcoinRoundLogo,
    },
    {
        currencyCode: "USDT",
        decimalScale: 6,
        sortScale: 1_000n,
        label: "USDt",
        icon: usdtRoundLogo,
    },
] as const;

const SYMBOL_IMAGES: Record<SlotSymbol, string> = {
    "swimming-trunks": symbolSwimmingTrunks,
    "coconut-drink": symbolCoconutDrink,
    sunglasses: symbolSunglasses,
    "beach-ball": symbolBeachBall,
    starfish: symbolStarfish,
    shell: symbolShell,
    "golden-beach-coins": symbolGoldenBeachCoins,
    "energy-drink": symbolEnergyDrink,
    "golden-shell": symbolGoldenShell,
    thief: symbolThief,
    "friend-steal": symbolFriendSteal,
};

function particleImageForPrize(
    prize: SpinPrize,
    reels: SpinResult["reels"],
): string {
    switch (prize.currency) {
        case "beach-coins":
            return symbolSingleBeachCoin;
        case "energy-drinks":
            return symbolEnergyDrink;
        case "usdt":
            return usdtRoundLogo;
        case "notcoin":
            return notcoinRoundLogo;
        case "gram":
            return gramRoundLogo;
        default:
            return reels.every((symbol) => symbol === reels[0])
                ? SYMBOL_IMAGES[reels[0]]
                : beachCoinsIcon;
    }
}

function isThreeFriendStealResult(reels: SpinResult["reels"]): boolean {
    return reels.every((symbol) => symbol === "friend-steal");
}

function particleScaleForPrize(
    prize: SpinPrize,
    reels: SpinResult["reels"],
): number {
    // Both reward WebPs use a 1280px square canvas, but the can artwork is
    // considerably narrower than the coin. Compensate for its smaller visible
    // footprint instead of changing the shared particle animation.
    if (prize.currency === "energy-drinks") return 0.8;
    if (prize.currency === "friend-steal" && isThreeFriendStealResult(reels)) {
        return FRIEND_STEAL_PARTICLE_SCALE;
    }
    return 0.3;
}

function particleCountForPrize(prize: SpinPrize): number {
    const amount = Math.abs(prize.amount);

    if (prize.currency === "beach-coins") {
        // ___the paytable spans 250K–150M. A logarithmic curve makes each
        // rarity tier visibly richer without letting large balances create
        // hundreds of sprites: 20 particles at 250K, 80 at 150M.
        const minimumPrize = 250_000;
        const maximumPrize = 150_000_000;
        const progress = Math.min(
            1,
            Math.max(
                0,
                Math.log(Math.max(amount, minimumPrize) / minimumPrize) /
                    Math.log(maximumPrize / minimumPrize),
            ),
        );
        return Math.round((20 + progress * 60) / 5) * 5;
    }

    if (prize.currency === "energy-drinks") {
        return Math.min(80, Math.max(20, Math.round(amount * 10)));
    }

    // On-chain prizes use a fixed, modest burst. Their decimal face value is
    // unrelated to visual intensity, and a hard cap keeps the effect cheap in
    // Telegram's mobile WebView.
    return 20;
}

type SpinState = "" | "spin" | "finish-spin";

interface ReelCell {
    symbol: SlotSymbol;
    above: SlotSymbol;
    below: SlotSymbol;
    spinState: SpinState;
    delayMs: number;
    durationMs: number | null;
}

function initialCells(): ReelCell[] {
    return buildReelDisplays().map((display) => ({
        symbol: display.symbol,
        above: display.above,
        below: display.below,
        spinState: "" as SpinState,
        delayMs: display.animationDelayMs,
        durationMs: null,
    }));
}

interface PrizeDisplay {
    icon: string | null;
    fallbackSymbol: string;
    amount: string;
    ariaLabel: string;
}

interface SlotMachineProps {
    onRequestEnergy: () => void;
    onFriendStealPresentation: (
        availableGrantCount: number,
        awarded: boolean,
    ) => void;
    spinTogether: SpinTogetherController;
}

type SettledSpinResponse = ClientSpinResponse;

function formatPrizeAmount(value: number, locale: string): string {
    return value.toLocaleString(locale, { maximumFractionDigits: 9 });
}

function formatBoostPercentageValue(
    basisPoints: number,
    locale: string,
): string {
    return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
    }).format(basisPoints / 100);
}

function prizeDisplay(
    prize: SpinPrize,
    t: TFunction,
    locale: string,
): PrizeDisplay | null {
    switch (prize.currency) {
        case "beach-coins":
            // ___negative = a thief steal; show it as a loss
            return {
                icon: beachCoinsIcon,
                fallbackSymbol: "BC",
                amount: `${prize.amount < 0 ? "-" : "+"}${formatCompact(Math.abs(prize.amount), locale)}`,
                ariaLabel:
                    prize.amount < 0
                        ? t("slotMachine.prize.lostBeachCoins", {
                              amount: formatCompact(
                                  Math.abs(prize.amount),
                                  locale,
                              ),
                          })
                        : t("slotMachine.prize.wonBeachCoins", {
                              amount: formatCompact(prize.amount, locale),
                          }),
            };
        case "energy-drinks":
            return {
                icon: symbolEnergyDrink,
                fallbackSymbol: "E",
                amount: `+${prize.amount}`,
                ariaLabel: t("slotMachine.prize.wonEnergy", {
                    amount: prize.amount,
                }),
            };
        case "friend-steal":
            return {
                icon: symbolFriendSteal,
                fallbackSymbol: "FS",
                amount:
                    prize.amount === 1
                        ? "+1"
                        : t("slotMachine.prize.inventoryFull"),
                ariaLabel:
                    prize.amount === 1
                        ? t("slotMachine.prize.wonFriendSteal")
                        : t("slotMachine.prize.friendStealInventoryFull"),
            };
        case "usdt":
            return {
                icon: usdtRoundLogo,
                fallbackSymbol: "USDt",
                amount: `+${formatPrizeAmount(prize.amount, locale)}`,
                ariaLabel: t("slotMachine.prize.wonCurrency", {
                    amount: formatPrizeAmount(prize.amount, locale),
                    currency: "USDt",
                }),
            };
        case "notcoin":
            return {
                icon: notcoinRoundLogo,
                fallbackSymbol: "NOT",
                amount: `+${formatPrizeAmount(prize.amount, locale)}`,
                ariaLabel: t("slotMachine.prize.wonCurrency", {
                    amount: formatPrizeAmount(prize.amount, locale),
                    currency: "Notcoin",
                }),
            };
        case "gram":
            return {
                icon: gramRoundLogo,
                fallbackSymbol: "GRAM",
                amount: `+${formatPrizeAmount(prize.amount, locale)}`,
                ariaLabel: t("slotMachine.prize.wonCurrency", {
                    amount: formatPrizeAmount(prize.amount, locale),
                    currency: "GRAM",
                }),
            };
        default:
            return null;
    }
}

function serverSpinResult(
    response: Extract<ApiSmSpinRes, { status: "OK" }>,
): SpinResult {
    const prize = response.outcome.prize;
    let mappedPrize: SpinPrize = { currency: null, amount: 0 };
    if (prize.kind === "ENERGY") {
        mappedPrize = {
            currency: "energy-drinks",
            amount: Number(prize.amount),
        };
    } else if (prize.kind === "CURRENCY") {
        switch (prize.currencyCode) {
            case "BEACH_COINS":
                mappedPrize = {
                    currency: "beach-coins",
                    amount: Number(prize.amount),
                };
                break;
            case "GRAM":
                mappedPrize = {
                    currency: "gram",
                    amount: Number(prize.amount) / 1_000_000_000,
                };
                break;
            case "NOT":
                mappedPrize = {
                    currency: "notcoin",
                    amount: Number(prize.amount) / 1_000_000_000,
                };
                break;
            case "USDT":
                mappedPrize = {
                    currency: "usdt",
                    amount: Number(prize.amount) / 1_000_000,
                };
                break;
        }
    } else if (prize.kind === "INVENTORY") {
        mappedPrize = {
            currency: "friend-steal",
            amount:
                response.friendStealAward?.result === "AWARDED"
                    ? response.friendStealAward.grantedCount
                    : 0,
        };
    }
    return {
        outcomeId: response.outcome.key,
        reels: response.outcome.reels as [SlotSymbol, SlotSymbol, SlotSymbol],
        prize: mappedPrize,
    };
}

function refillCountdown(nextRefillAt: string | null | undefined, now: number) {
    if (!nextRefillAt) return null;
    const remaining = Math.max(0, new Date(nextRefillAt).getTime() - now);
    if (remaining === 0) return "00:00";
    const totalSeconds = Math.ceil(remaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return hours > 0
        ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
        : `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export default function SlotMachine({
    onRequestEnergy,
    onFriendStealPresentation,
    spinTogether,
}: SlotMachineProps) {
    const { t, i18n } = useTranslation();
    const locale = i18n.resolvedLanguage ?? i18n.language ?? "en";
    const { state: authState } = useSlice("authSlice");
    const isAuthReady = authState.bootstrapStatus === "ready";
    const slot = useSlotMachine(isAuthReady);
    const [cells, setCells] = useState<ReelCell[]>(initialCells);
    const [pot, setPot] = useState<SpinPrize | null>(null);
    const [potBoost, setPotBoost] = useState<ApiSmSpinTogetherBoost | null>(
        null,
    );
    const [isSpinning, setIsSpinning] = useState(false);
    const [completedSpinUserId, setCompletedSpinUserId] = useState<string | null>(null);
    const bet = useBetMultiplier(
        slot.snapshot?.player.energy,
        slot.snapshot?.campaign.betting,
        () => toast(t("slotMachine.controls.betLocked")),
    );
    const nextSpinAtRef = useRef(0);
    const [displayedSnapshot, setDisplayedSnapshot] = useState<
        ApiSmStateSnapshot | undefined
    >(slot.snapshot);
    const [isJackpotCelebration, setIsJackpotCelebration] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isWithdrawalOpen, setIsWithdrawalOpen] = useState(false);
    const [initialWithdrawalCurrency, setInitialWithdrawalCurrency] =
        useState<ApiWithdrawalCurrencyCode>();
    const closeSettings = useCallback(() => setIsSettingsOpen(false), []);
    const openWithdrawals = useCallback(() => {
        setInitialWithdrawalCurrency(undefined);
        setIsWithdrawalOpen(true);
    }, []);
    const openWithdrawalsForCurrency = useCallback(
        (currencyCode: ApiWithdrawalCurrencyCode) => {
            setInitialWithdrawalCurrency(currencyCode);
            setIsWithdrawalOpen(true);
        },
        [],
    );
    const closeWithdrawals = useCallback(() => setIsWithdrawalOpen(false), []);
    const [chaseNotice, setChaseNotice] = useState<ApiSmSpinChaseResult | null>(
        null,
    );
    const [now, setNow] = useState(Date.now);
    const spinningRef = useRef(false);
    const energyRequestRef = useRef(false);
    const timersRef = useRef<number[]>([]);
    const chaseNoticeTimerRef = useRef<number | null>(null);
    const stopFriendStealMusicRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);

    // The hook commits authoritative spin state as soon as the API responds so
    // request sequencing remains correct. Keep presentation on the previous
    // snapshot until the reels settle, otherwise the HUD reveals the prize.
    // Report the same delayed inventory count to MainPage's launcher.
    useEffect(() => {
        if (!isSpinning && slot.snapshot) {
            setDisplayedSnapshot(slot.snapshot);
            onFriendStealPresentation(
                slot.snapshot.friendStealInventory.availableGrantCount,
                false,
            );
        }
    }, [isSpinning, onFriendStealPresentation, slot.snapshot]);

    useEffect(() => {
        const nextRefillAt = slot.snapshot?.player.nextRefillAt;
        if (!nextRefillAt) return;
        const delay = Math.max(
            0,
            new Date(nextRefillAt).getTime() - Date.now() + 250,
        );
        const timer = window.setTimeout(() => void slot.refetch(), delay);
        return () => window.clearTimeout(timer);
    }, [slot.snapshot?.player.nextRefillAt, slot.refetch]);

    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            timers.forEach((id) => window.clearTimeout(id));
            if (chaseNoticeTimerRef.current !== null) {
                window.clearTimeout(chaseNoticeTimerRef.current);
            }
            stopFriendStealMusicRef.current?.();
        };
    }, []);

    // ___fetch + decode all SFX and the friend-steal music once up front
    // (alpha-mini-app: preloadSounds on component init), shared for the
    // component's lifetime.
    useEffect(() => {
        preloadSounds(ALL_SFX);
    }, []);

    // ___alpha-mini-app initializes its fullscreen Pixi particle canvas and reward
    // textures before the first payout so the initial burst is not delayed by
    // renderer or image setup.
    useEffect(() => {
        void preloadPrizeParticleImages([
            symbolSingleBeachCoin,
            symbolEnergyDrink,
            symbolFriendSteal,
            ...ONCHAIN_PRIZE_PARTICLE_IMAGES,
        ]).catch((error) => {
            console.error("[prize-particles] preload failed", error);
        });
        return () => clearPrizeParticleCluster(SLOT_PRIZE_PARTICLE_CLUSTER);
    }, []);

    const schedule = useCallback((fn: () => void, ms: number) => {
        const timer = window.setTimeout(() => {
            const index = timersRef.current.indexOf(timer);
            if (index >= 0) timersRef.current.splice(index, 1);
            fn();
        }, ms);
        timersRef.current.push(timer);
    }, []);

    const sleep = useCallback(
        (ms: number) => new Promise<void>((resolve) => schedule(resolve, ms)),
        [schedule],
    );

    const setCell = useCallback((index: number, patch: Partial<ReelCell>) => {
        setCells((current) =>
            current.map((cell, i) =>
                i === index ? { ...cell, ...patch } : cell,
            ),
        );
    }, []);

    // ___alpha-mini-app win vibration: ["light","heavy","light","heavy","light","rigid"]
    const playWinHaptics = useCallback(() => {
        const pattern = [
            "light",
            "heavy",
            "light",
            "heavy",
            "light",
            "rigid",
        ] as const;
        pattern.forEach((style, i) =>
            schedule(() => haptics.impact(style), i * 120),
        );
    }, [schedule]);

    const spin = useCallback(async (settled?: SettledSpinResponse): Promise<boolean> => {
        if (spinningRef.current) return false;
        const snapshot = slot.snapshot;
        if (!snapshot) {
            haptics.notification("error");
            return false;
        }
        if (
            !settled &&
            !hasSpinEnergy(snapshot, bet.multiplier)
        ) {
            haptics.notification("error");
            return false;
        }

        spinningRef.current = true;
        const spinStartedAt = Date.now();
        spinTogether.reportActivity("SPINNING");
        setIsSpinning(true);
        setChaseNotice(null);
        if (chaseNoticeTimerRef.current !== null) {
            window.clearTimeout(chaseNoticeTimerRef.current);
            chaseNoticeTimerRef.current = null;
        }
        haptics.impact("light");
        // ___reels-whirring loop kickoff (alpha-mini-app: slots.mp3 on spin start)
        playSound(sfxReelsWhirring, 0, false, WHIRRING_VOLUME);

        // ___all reels into the blurred infinite loop while the outcome
        // resolves (alpha-mini-app: resetSpinState("spin"))
        setCells((current) =>
            current.map((cell) => ({
                ...cell,
                spinState: "spin" as SpinState,
                durationMs: null,
            })),
        );

        let response: SettledSpinResponse;
        try {
            [response] = await Promise.all([
                settled ?? slot.spin(bet.multiplier),
                sleep(SPIN_RESOLVE_DELAY_MS),
            ]);
        } catch (error) {
            setCells((current) =>
                current.map((cell) => ({
                    ...cell,
                    spinState: "" as SpinState,
                    durationMs: null,
                })),
            );
            haptics.notification("error");
            spinningRef.current = false;
            setIsSpinning(false);
            spinTogether.reportActivity("IDLE");
            if (
                error instanceof SlotMachineApiError &&
                error.response?.code === "INSUFFICIENT_ENERGY" &&
                !error.response.commercePresentation?.autoOpen
            ) {
                onRequestEnergy();
            }
            return false;
        }
        const result = serverSpinResult(response);

        // ___swap in the outcome symbols mid-blur; the 0/50/100ms delays
        // only offset the loop phase per reel
        const displays = buildReelDisplays(result, true);
        setCells(
            displays.map((display) => ({
                symbol: display.symbol,
                above: display.above,
                below: display.below,
                spinState: "spin" as SpinState,
                delayMs: display.animationDelayMs,
                durationMs: null,
            })),
        );

        // ___staggered stops; while a high-value streak is alive, delays and
        // durations stretch by random factors for suspense (alpha-mini-app' exact
        // "ass" near-miss algorithm, including taking the total wait from the
        // last reel's numbers only). The streak symbol is reel 0's, if it's a
        // high-value one; any such streak drives the escalation stingers, but
        // the jackpot music + scale-up celebration stay golden-shell only.
        const streakSymbol = ESCALATION_SYMBOLS.includes(result.reels[0])
            ? result.reels[0]
            : null;
        let streakAlive = streakSymbol !== null;
        let finalWaitMs = 0;
        for (let i = 0; i < 3; i++) {
            let delay: number = REEL_STOP_DELAYS_MS[i]!;
            let duration: number = REEL_STOP_DURATIONS_MS[i]!;
            let holdMs = 0;
            const reelNo = i + 1;
            const nearMiss = streakAlive && reelNo >= 2;
            if (streakAlive) {
                streakAlive = result.reels[i] === streakSymbol;
            }
            const isStreakReel = streakAlive;
            const isJackpotReel =
                isStreakReel && streakSymbol === JACKPOT_SYMBOL;
            if (nearMiss || streakAlive) {
                delay *= getRandom(reelNo, 10);
                duration *= getRandom(1, 1.1 * reelNo);
                // ___extra hold before the win banner only for the actual
                // jackpot's scale-up celebration
                if (isJackpotReel && reelNo === 3) holdMs = JACKPOT_HOLD_MS;
                // ___the stretched reel-2 stop outlasts the whirring sample,
                // so restart it for tension (alpha-mini-app replays slots.mp3 here)
                if (streakAlive && reelNo === 2) {
                    playSound(sfxReelsWhirring, 0, false, WHIRRING_VOLUME);
                }
            }
            finalWaitMs = delay + duration + holdMs;

            // ___stop "clunk" per reel, pitch-randomized so the three stops
            // don't sound identical; streak reels swap in the escalation
            // stingers instead (alpha-mini-app: slotstopped vs ass-1/2/3)
            const stopSfx = isStreakReel ? SFX_ESCALATIONS[i]! : sfxReelStop;
            const settleMs = duration;
            schedule(() => {
                setCell(i, {
                    spinState: "finish-spin",
                    delayMs: 0,
                    durationMs: settleMs,
                });
                schedule(() => {
                    haptics.impact("light");
                    playSound(stopSfx, 0, !isStreakReel);
                    if (isJackpotReel) {
                        // ___clear the animation so the celebration scale-up
                        // transition can take over (alpha-mini-app: spinState="")
                        setCell(i, { spinState: "" });
                    }
                    if (isStreakReel && reelNo === 3) {
                        // ___the escalation sequence resolved on a full
                        // high-value 3-of-a-kind (shell / golden-beach-coins /
                        // jackpot), so bring in the triumphant post-escalation
                        // music (alpha-mini-app: hallelujah.mp3)
                        playSound(sfxPostEscalation);
                        if (isJackpotReel) {
                            schedule(() => setIsJackpotCelebration(true), 50);
                        }
                    }
                }, settleMs);
            }, delay);
        }

        await sleep(finalWaitMs);
        // await sleep(10);
        await sleep(5);
        setCells((current) =>
            current.map((cell) => ({
                ...cell,
                spinState: "" as SpinState,
                durationMs: null,
            })),
        );
        setDisplayedSnapshot((current) =>
            mergeSpinResponseSnapshot(current, response),
        );
        onFriendStealPresentation(
            response.snapshot.friendStealInventory.availableGrantCount,
            result.prize.currency === "friend-steal" &&
                result.prize.amount > 0,
        );
        // Keep the previous prize visible until the new outcome is revealed.
        setPot(result.prize.currency ? result.prize : null);
        setPotBoost(response.spinTogetherBoost ?? null);

        if (result.outcomeId === "three-thief") {
            // ___the thief stole beach-coins: show the loss banner with a
            // villain taunt and a negative (error) buzz instead of the win feel
            playSound(sfxEvilLaugh);
            haptics.notification("error");
        } else if (result.prize.currency) {
            playWinHaptics();
            const wonThreeFriendSteals = isThreeFriendStealResult(result.reels);
            const friendStealWasAwarded =
                response.friendStealAward?.result === "AWARDED";
            if (wonThreeFriendSteals && friendStealWasAwarded) {
                stopFriendStealMusicRef.current?.();
                stopFriendStealMusicRef.current = playTimedFadeSound(
                    friendStealMusic,
                    {
                        durationMs: FRIEND_STEAL_MUSIC_DURATION_MS,
                        fadeOutMs: FRIEND_STEAL_MUSIC_FADE_OUT_MS,
                    },
                );
            }
            const payoutSound =
                result.prize.currency === "energy-drinks"
                    ? sfxEnergyWin
                    : sfxCoinsFalling;

            // ___alpha-mini-app' "slots" particle cluster: reward sprites burst from
            // the spin control toward the camera for ~700ms. The payout sound
            // lands once roughly 75% of the sprites have left the screen.
            void generatePrizeParticles({
                cluster: SLOT_PRIZE_PARTICLE_CLUSTER,
                particleCount: particleCountForPrize(result.prize),
                particleImages: [
                    particleImageForPrize(result.prize, result.reels),
                ],
                scale: particleScaleForPrize(result.prize, result.reels),
                delayMs: 1,
                startElementSelector: "#slot-particle-source",
                onAlmostFinished: () => playSound(payoutSound),
            })
                .then((started) => {
                    if (!started) playSound(payoutSound);
                })
                .catch((error) => {
                    console.error("[prize-particles] burst failed", error);
                    playSound(payoutSound);
                });
        }
        if (result.outcomeId === "jackpot") {
            schedule(() => setIsJackpotCelebration(false), 1500);
        }
        if (!response.replayed && response.chase) {
            const unlockedMilestone = response.chase.milestoneGrants.length > 0;
            if (unlockedMilestone) {
                setChaseNotice(response.chase);
                chaseNoticeTimerRef.current = window.setTimeout(() => {
                    setChaseNotice(null);
                    chaseNoticeTimerRef.current = null;
                }, 4200);
            }
            if (unlockedMilestone && !result.prize.currency) {
                playWinHaptics();
                playSound(
                    response.chase.milestoneGrants.some(
                        (grant) => grant.reward.kind === "SM_ENERGY",
                    )
                        ? sfxEnergyWin
                        : sfxCoinsFalling,
                );
            }
        }

        nextSpinAtRef.current = Date.now() + autospinDelay(Date.now() - spinStartedAt, finalWaitMs);
        setCompletedSpinUserId(authState.userId);
        spinningRef.current = false;
        setIsSpinning(false);
        spinTogether.reportActivity("IDLE");
        return true;
    }, [
        authState.userId,
        bet.multiplier,
        onFriendStealPresentation,
        onRequestEnergy,
        playWinHaptics,
        schedule,
        setCell,
        sleep,
        spinTogether,
        slot,
    ]);

    const hasEnergy = hasSpinEnergy(slot.snapshot, bet.multiplier);
    const outOfEnergy = !hasEnergy;

    const handleSpinClick = useCallback(async (): Promise<boolean> => {
        if (spinningRef.current || energyRequestRef.current) return false;
        if (outOfEnergy) {
            haptics.impact("light");
            energyRequestRef.current = true;
            try {
                // The server chooses an eligible offer. A refill may have
                // landed meanwhile: animate that settled spin without retrying it.
                const response = await slot.spin(bet.multiplier);
                return await spin(response);
            } catch (error) {
                if (
                    !(error instanceof SlotMachineApiError) ||
                    !error.response?.commercePresentation?.autoOpen
                ) {
                    onRequestEnergy();
                }
            } finally {
                energyRequestRef.current = false;
            }
            return false;
        }

        return spin();
    }, [bet.multiplier, onRequestEnergy, outOfEnergy, slot, spin]);

    const spinUnavailable =
        slot.isLoading ||
        !isAuthReady ||
        !slot.snapshot ||
        Boolean(slot.loadError);
    const autospin = useAutospin({
        canSpin: !spinUnavailable && !isSettingsOpen && !isWithdrawalOpen,
        isBusy: isSpinning || slot.isSpinning,
        spin: handleSpinClick,
        nextSpinDelay: () => Math.max(0, nextSpinAtRef.current - Date.now()),
    });

    const wonPrize = pot ? prizeDisplay(pot, t, locale) : null;
    useTelegramWriteAccess(
        completedSpinUserId,
        isSpinning || slot.isSpinning || autospin.isAutoSpinning || isJackpotCelebration,
    );
    usePlayerFeedback(
        completedSpinUserId,
        isSpinning || slot.isSpinning || autospin.isAutoSpinning ||
            isJackpotCelebration || isSettingsOpen || isWithdrawalOpen ||
            chaseNotice !== null || slot.isLoading,
    );
    const boostLabel = potBoost
        ? `+${formatBoostPercentageValue(potBoost.basisPoints, locale)}%`
        : null;
    // ___a thief steal is a negative beach-coins "prize"; style it as a loss
    const isStealPrize = pot?.currency === "beach-coins" && pot.amount < 0;
    // ___alpha-mini-app drives both tickers off the first reel's spin state
    const tickerState = cells[0]?.spinState ?? "";
    const beachCoins =
        displayedSnapshot?.balances.find(
            (balance) => balance.currencyCode === "BEACH_COINS",
        )?.balance ?? "0";
    const energyAmount = displayedSnapshot?.player.energy ?? "0";
    const onchainBalances = useMemo(() => {
        const balancesByCurrency = new Map(
            displayedSnapshot?.balances.map((balance) => [
                balance.currencyCode,
                balance.balance,
            ]),
        );

        return ONCHAIN_BALANCE_ASSETS.map((asset, stableIndex) => {
            const balance = balancesByCurrency.get(asset.currencyCode) ?? "0";
            return {
                ...asset,
                balance,
                displayAmount: formatHudMinorUnits(
                    balance,
                    asset.decimalScale,
                    locale,
                ),
                normalizedAmount: BigInt(balance) * asset.sortScale,
                stableIndex,
            };
        }).sort((left, right) => {
            if (left.normalizedAmount > right.normalizedAmount) return -1;
            if (left.normalizedAmount < right.normalizedAmount) return 1;
            return left.stableIndex - right.stableIndex;
        });
    }, [displayedSnapshot?.balances, locale]);
    const countdown = refillCountdown(
        displayedSnapshot?.player.nextRefillAt,
        now,
    );
    const stateError =
        authState.bootstrapStatus === "no-telegram"
            ? t("slotMachine.state.openInTelegram")
            : authState.bootstrapStatus === "error"
              ? (authState.error ?? t("slotMachine.state.loginFailed"))
              : slot.loadError
                ? t("slotMachine.state.loadFailedRetry")
                : null;

    return (
        <div
            className={[
                "slot-machine",
                isJackpotCelebration && "slot-machine--jackpot",
            ]
                .filter(Boolean)
                .join(" ")}
        >
            <SoundButton
                type="button"
                className="slot-machine__settings-button"
                aria-label={t("slotMachine.settings")}
                onClick={() => setIsSettingsOpen(true)}
            >
                <FontAwesomeIcon icon={faGear} />
            </SoundButton>

            <div className="slot-machine__hud">
                <div className="slot-machine__balances">
                    <div className="slot-machine__balance">
                        <img src={beachCoinsIcon} alt="" draggable={false} />
                        <span
                            className="slot-machine__balance__amount"
                            dir="ltr"
                        >
                            {slot.isLoading
                                ? "…"
                                : formatCompact(Number(beachCoins), locale)}
                        </span>
                    </div>
                    <div className="slot-machine__balance">
                        <img src={symbolEnergyDrink} alt="" draggable={false} />
                        <span
                            className="slot-machine__balance__amount"
                            dir="ltr"
                        >
                            {slot.isLoading ? "…" : energyAmount}
                        </span>
                    </div>
                    {SHOW_ONCHAIN_BALANCES &&
                        onchainBalances.map((balance) => (
                            <SoundButton
                                type="button"
                                className="slot-machine__balance is-withdrawable"
                                key={balance.currencyCode}
                                aria-label={t("slotMachine.balance", {
                                    currency: balance.label,
                                    amount: balance.displayAmount,
                                })}
                                onClick={() =>
                                    openWithdrawalsForCurrency(balance.currencyCode)
                                }
                                disabled={
                                    !isAuthReady || !slot.snapshot || isSpinning
                                }
                            >
                                <img
                                    src={balance.icon}
                                    alt=""
                                    aria-hidden="true"
                                    draggable={false}
                                />
                                <span
                                    className="slot-machine__balance__amount"
                                    dir="ltr"
                                >
                                    {slot.isLoading ? "…" : balance.displayAmount}
                                </span>
                            </SoundButton>
                        ))}
                </div>
                <SoundButton
                    type="button"
                    className="slot-machine__withdraw-button"
                    aria-label={t("slotMachine.withdraw")}
                    onClick={openWithdrawals}
                    disabled={!isAuthReady || !slot.snapshot || isSpinning}
                >
                    <FontAwesomeIcon icon={faMoneySimpleFromBracket} />
                </SoundButton>
            </div>

            {stateError && (
                <SoundButton
                    type="button"
                    className="slot-machine__state-error"
                    onClick={() => {
                        if (isAuthReady) {
                            void slot.refetch();
                        }
                    }}
                    disabled={!isAuthReady}
                >
                    {stateError}
                </SoundButton>
            )}

            <div className="slot-machine__stage">
                {/* ___"PEBBLER" title banner; sits behind the wooden board
                    and peeks out above its top edge */}
                <img
                    className="slot-machine__title"
                    src={slotMachineTitle}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                />
                <img
                    className="slot-machine__board"
                    src={woodenBoard}
                    alt=""
                    draggable={false}
                />

                {/* {slot.snapshot?.chase && (
                    <SymbolChase
                        chase={slot.snapshot.chase}
                        notice={chaseNotice}
                    />
                )} */}

                {displayedSnapshot?.chase ? (
                    <SymbolChase
                        chase={displayedSnapshot.chase}
                        notice={chaseNotice}
                    />
                ) : (
                    // <h1>{t("slotMachine.noChase")}</h1>
                    <></>
                )}

                <div
                    className={[
                        "slot-machine__prize",
                        isStealPrize && "slot-machine__prize--steal",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                    role="status"
                    aria-live="polite"
                    aria-label={[wonPrize?.ariaLabel, boostLabel]
                        .filter(Boolean)
                        .join(". ")}
                >
                    {wonPrize && (
                        <>
                            {wonPrize.icon ? (
                                <img
                                    className="slot-machine__prize__symbol"
                                    src={wonPrize.icon}
                                    alt=""
                                    aria-hidden="true"
                                    draggable={false}
                                />
                            ) : (
                                <span
                                    className="slot-machine__prize__symbol-code"
                                    aria-hidden="true"
                                >
                                    {wonPrize.fallbackSymbol}
                                </span>
                            )}
                            <span
                                className="slot-machine__prize__amount"
                                dir="ltr"
                            >
                                {wonPrize.amount}
                            </span>
                            {boostLabel && (
                                <span
                                    className="slot-machine__prize__boost"
                                    data-text={boostLabel}
                                    dir="ltr"
                                >
                                    <span className="slot-machine__prize__boost-text">
                                        {boostLabel}
                                    </span>
                                </span>
                            )}
                        </>
                    )}
                </div>

                <div className="slot-machine__machine">
                    <div className="slot-machine__reels">
                        {cells.map((cell, i) => (
                            <div
                                key={i}
                                className={[
                                    "slot-reel",
                                    `slot-reel--${i}`,
                                    cell.symbol === JACKPOT_SYMBOL &&
                                        "is-jackpot-symbol",
                                    cell.spinState,
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                style={
                                    {
                                        "--icon-above": `url("${SYMBOL_IMAGES[cell.above]}")`,
                                        "--icon-below": `url("${SYMBOL_IMAGES[cell.below]}")`,
                                        backgroundImage: `url("${SYMBOL_IMAGES[cell.symbol]}")`,
                                        animationDelay: `${cell.delayMs}ms`,
                                        animationDuration:
                                            cell.durationMs !== null
                                                ? `${cell.durationMs}ms`
                                                : undefined,
                                    } as CSSProperties
                                }
                            />
                        ))}
                    </div>
                    <img
                        className="slot-machine__frame"
                        src={slotMachineFrame}
                        alt=""
                        draggable={false}
                    />
                    <BetMultiplierBanner
                        multiplier={bet.multiplier}
                        visible={bet.isBannerVisible}
                    />

                    {/* ___side tickers, flicked by the reels while they spin
                        (alpha-mini-app binds these to the first reel's spin state) */}
                    <div
                        className={`slot-machine__ticker slot-machine__ticker--left ${tickerState}`}
                    >
                        <img
                            className="slot-machine__ticker__img"
                            src={tickerLeft}
                            alt=""
                            draggable={false}
                        />
                    </div>
                    <div
                        className={`slot-machine__ticker slot-machine__ticker--right ${tickerState}`}
                    >
                        <img
                            className="slot-machine__ticker__img"
                            src={tickerRight}
                            alt=""
                            draggable={false}
                        />
                    </div>
                </div>

                {spinTogether.enabled && spinTogether.config && (
                    <SpinTogetherBoostIndicator
                        className="slot-machine__boost"
                        config={spinTogether.config}
                        friends={spinTogether.friends}
                        locale={locale}
                    />
                )}
            </div>

            <div className="slot-machine__controls">
                <span
                    id="slot-particle-source"
                    className="slot-machine__particle-source"
                    aria-hidden="true"
                />
                <SpinButton
                    disabled={spinUnavailable}
                    isBusy={isSpinning || slot.isSpinning}
                    isAutoSpinning={autospin.isAutoSpinning}
                    canStartAutospin={hasEnergy}
                    betMultiplier={bet.multiplier}
                    isMaxBet={bet.isMaxBet}
                    betDisabled={false}
                    spinLabel={
                        outOfEnergy ? t("mainPage.energy.ariaLabel") : undefined
                    }
                    onSpin={() => void autospin.spin()}
                    onStartAutospin={autospin.start}
                    onStopAutospin={autospin.stop}
                    onBetChange={bet.cycleMultiplier}
                />
            </div>

            {/* ___free hourly refill hint under the spin button; the two
                    texts are separate <p>s so their spacing can be tuned */}
            {countdown && displayedSnapshot && (
                <div className="slot-machine__refill">
                    <img
                        className="slot-machine__refill__icon"
                        src={symbolEnergyDrink}
                        alt=""
                        draggable={false}
                    />
                    <p className="slot-machine__refill__amount" dir="ltr">
                        +{displayedSnapshot.campaign.energyRefillAmount}
                    </p>
                    <p className="slot-machine__refill__timer">
                        {t("slotMachine.refillIn", { countdown })}
                    </p>
                </div>
            )}
            {spinTogether.enabled &&
                spinTogether.config &&
                spinTogether.friends.length > 0 && (
                    <div className="slot-machine__friends-container">
                        <FriendsRail
                            className="slot-machine__friends"
                            spinTogether={spinTogether}
                        />
                    </div>
                )}

            <SettingsDialog
                isOpen={isSettingsOpen}
                onClose={closeSettings}
                spinTogether={spinTogether}
            />

            <WithdrawalDialog
                isOpen={isWithdrawalOpen}
                initialCurrencyCode={initialWithdrawalCurrency}
                onOpen={openWithdrawals}
                onClose={closeWithdrawals}
                onBalancesChanged={slot.refetch}
            />
        </div>
    );
}
